import { useEffect } from 'react';
import { createPortal } from 'react-dom';

// Components
import IconButton from '../../atoms/IconButton/IconButton';
import cn from '../../../utils/cn';

// Style
import style from './Modal.module.css';

/**
 * Modal header with title and close button.
 * @param {object} props
 * @param {string} props.title
 * @param {() => void} props.onClose
 * @param {string} [props.titleId='modal-title']
 * @returns {JSX.Element}
 */
export function ModalHeader({
  title,
  onClose,
  titleId = 'modal-title',
}) {
  return (
    <div className={style['modal-header']}>
      <p id={titleId} className={style['modal-title']}>{title}</p>
      <IconButton
        color="no-background"
        icon="close-md"
        className={style.close}
        onClick={onClose}
        title="close"
      />
    </div>
  );
}

/**
 * Modal component - overlay shell with optional header slot
 * @param {object} props
 * @param {string} [props.id] - Modal id
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {() => void} props.onClose - Function to call when backdrop is clicked
 * @param {React.ReactNode} props.children - Modal content
 * @param {React.ReactNode} [props.header] - Optional header slot
 * @param {string} [props.className] - Additional CSS classes for the dialog panel
 * @param {string} [props.titleId='modal-title'] - id for aria-labelledby when header is provided
 * @returns {JSX.Element | null} Modal component
 */
function Modal({
  id = 'modal',
  isOpen,
  onClose,
  children,
  header = null,
  className = '',
  titleId = 'modal-title',
}) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (/** @type {KeyboardEvent} */ event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (/** @type {React.MouseEvent} */ e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className={style.backdrop}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className={cn([style.modal, className])}
        role="dialog"
        aria-modal="true"
        aria-labelledby={header ? titleId : undefined}
        id={id}
      >
        {header}
        <div className={cn([style['modal-content'], header && style['modal-content-with-header']])}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
